import { jsx as _jsx } from "react/jsx-runtime";
import { getScreeningDatesForQuery, isEmptyArray, loadScreeningDatesByQuery, logger } from '@vista-digital/occ-domain';
import { observer } from 'mobx-react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useI18n } from '../../../context/occ/hooks/use-i18n';
import { getDropdownOptionsForScreeningDates, ScreeningDateDropdownOptionTemplate, ScreeningDatePreviewDropdownOptionTemplate } from '../../../subcomponents/form/input/dropdown/option-templates/screening-date';
import { QuickNavigatorContextInstance } from '../types/context';
import { QuickNavigatorActionType } from '../utils/action-type';
import { DropdownType, QuickNavigatorDropdown } from './dropdown';
export const QuickNavigatorScreeningDatesDropdown = observer(() => {
    var _a;
    const i18n = useI18n();
    const { displayText, config, assets, state, dispatch } = useContext(QuickNavigatorContextInstance);
    const { enableSearch } = config.screeningDates;
    const selectedSite = state.dropdownSelections.site;
    const selectedFilm = state.dropdownSelections.film;
    const [messageType, setMessageType] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const query = useMemo(() => {
        if (!selectedSite)
            return undefined;
        return { siteIds: [selectedSite] };
    }, [selectedSite]);
    useEffect(() => {
        (async () => {
            if (!query)
                return;
            setIsLoading(true);
            try {
                await loadScreeningDatesByQuery(query);
                setMessageType(undefined);
            }
            catch (error) {
                logger.error(error);
                setMessageType(QuickNavigatorScreeningDateDropdownMessageType.FailedToLoadScreeningDates);
            }
            finally {
                setIsLoading(false);
            }
        })();
    }, [query]);
    const screeningDates = useMemo(() => {
        var _a;
        if (isLoading || !query)
            return [];
        const allScreeningDates = (_a = getScreeningDatesForQuery(query)) !== null && _a !== void 0 ? _a : [];
        return selectedFilm ? allScreeningDates.filter(s => s.filmScreenings.find(f => f.filmId === selectedFilm)) : [];
    }, [isLoading, query, selectedFilm]);
    const dropdownOptions = getDropdownOptionsForScreeningDates(screeningDates, i18n, {
        screeningDateFormat: displayText.screeningDateFormat,
        todayLabel: displayText.todayLabel
    });
    const today = (_a = dropdownOptions.find(option => !!option.todayLabel)) === null || _a === void 0 ? void 0 : _a.value;
    useEffect(() => {
        if (today && typeof today === 'string') {
            dispatch({
                type: QuickNavigatorActionType.SetSelectedValue,
                dropdownType: DropdownType.ScreeningDate,
                selectedValue: today
            });
        }
    }, [dispatch, today]);
    useEffect(() => {
        if (isEmptyArray(screeningDates) && !isLoading && !messageType) {
            setMessageType(QuickNavigatorScreeningDateDropdownMessageType.NoScreeningDates);
        }
    }, [screeningDates, isLoading, messageType]);
    const messageProps = getQuickNavigatorScreeningDateDropdownMessageProps(messageType, assets, displayText);
    const isDisabled = !selectedSite || !selectedFilm || isLoading;
    return (_jsx(QuickNavigatorDropdown, Object.assign({}, messageProps, { type: DropdownType.ScreeningDate, options: dropdownOptions, loading: isLoading, disabled: isDisabled, enableSearch: enableSearch, placeholder: displayText.screeningDateDropdownPlaceholder, label: displayText.screeningDateDropdownLabel, modal: Object.assign(Object.assign({}, messageProps === null || messageProps === void 0 ? void 0 : messageProps.modal), { displayAsModalOnMobile: config.screeningDates.displayAsModalOnMobile, heading: displayText.screeningDateModalHeading, searchInputPlaceholder: displayText.screeningDateModalSearchInputPlaceholder, noSearchResultsFoundMessage: displayText.noScreeningDateSearchResultsFoundModalMessage, noSearchResultsFoundIconName: assets.noScreeningDatesFoundModalIconName }), optionTemplate: ScreeningDateDropdownOptionTemplate, optionPreviewTemplate: ScreeningDatePreviewDropdownOptionTemplate, noSearchResultsFoundMessage: displayText.noScreeningDatesFoundErrorMessage })));
});
var QuickNavigatorScreeningDateDropdownMessageType;
(function (QuickNavigatorScreeningDateDropdownMessageType) {
    QuickNavigatorScreeningDateDropdownMessageType["FailedToLoadScreeningDates"] = "FailedToLoadScreeningDates";
    QuickNavigatorScreeningDateDropdownMessageType["NoScreeningDates"] = "NoScreeningDates";
})(QuickNavigatorScreeningDateDropdownMessageType || (QuickNavigatorScreeningDateDropdownMessageType = {}));
const getQuickNavigatorScreeningDateDropdownMessageProps = (messageType, assets, displayText) => {
    switch (messageType) {
        case QuickNavigatorScreeningDateDropdownMessageType.FailedToLoadScreeningDates:
            return {
                errorMessageHelpText: displayText.failedToLoadScreeningDatesErrorMessage,
                modal: {
                    errorTitle: displayText.failedToLoadScreeningDatesModalErrorTitle,
                    errorMessage: displayText.failedToLoadScreeningDatesModalErrorMessage,
                    errorIconName: assets.failedToLoadScreeningDatesModalErrorIconName
                }
            };
        case QuickNavigatorScreeningDateDropdownMessageType.NoScreeningDates:
            return {
                modal: {
                    errorTitle: displayText.noScreeningDatesModalErrorTitle,
                    errorMessage: displayText.noScreeningDatesModalErrorMessage,
                    errorIconName: assets.noScreeningDatesModalErrorIconName
                }
            };
        default:
            return undefined;
    }
};
//# sourceMappingURL=screening-date-dropdown.js.map